import { humanizeSlug } from "utils/strings";

/**
 * UI-local node card section structure, keyed by collection abbreviation.
 *
 * This is a presentation-only config owned by the UI. It is intentionally
 * separate from `assets/nlm-ckn-collection-maps.json`, which is kept
 * byte-identical with the ETL repo by a sync workflow and must not carry
 * UI section tags.
 *
 * Each section may carry static `description` text (rendered even when the
 * section has no rows) and `info`, a tooltip on an icon beside the heading.
 *
 * Each field descriptor:
 *   - key:     the document property to read.
 *   - label:   the display label shown in the card (authoritative).
 *   - value:   optional; a fixed value shown instead of the document's.
 *   - variant: optional; "description" renders as a lead paragraph, not a row.
 *
 * Values and URLs for keys that also appear in the collection map are resolved
 * through `getDisplayFields` (so URL templates stay in one place). Keys not in
 * the collection map resolve their plain value straight from the document; if a
 * key is absent in the data, that field simply does not render.
 */
/**
 * The filtering criteria section is static text: it describes how CKN selects
 * cells rather than reporting anything the document carries, and reads the
 * same on every card that shows it.
 */
const CKN_FILTERING_CRITERIA = {
  section: "CKN Filtering Criteria",
  info: "Criteria used in CKN to filter human normal adult cells",
  description:
    "Only human normal adult cells are included in CKN v1.0. Cells are filtered based on NCBI " +
    "Taxonomy ID for species, UBERON IDs for tissues traced back to the parent term of the " +
    "Anatomical Structure Collection, Human Developmental Stages (HSAPDV) Ontology for age, and " +
    "the Phenotype And Trait Ontology PATO:0000461 for normal or healthy tissue.",
  fields: [],
};

/**
 * Info-icon text the node card sheet (09/16, nlm-ckn#335) gives the marker and
 * score sections. Shared by every card that shows those sections.
 */
const MARKERS_INFO =
  "Biomarker combination is the minimal combination of necessary and sufficient marker genes " +
  "generated by the NS-Forest algorithm (Aevermann et al. 2021, Liu et al. 2024). Binary gene set " +
  "is the top ranked genes with the highest binary scores defined in NS-Forest. NS-Forest " +
  "algorithm is applied on the post-filtering dataset.";
const BIOMARKER_METRICS_INFO =
  "Performance metrics of the biomarker combination reported by NS-Forest. The F-beta score is " +
  "also used as a quality control metric for the cell set quality, indicating whether good " +
  "markers exist for characterizing this cell set.";
const BINARY_SCORE_INFO =
  "Binary score is a metric that quantifies the binaryness of gene expression of a gene in the " +
  "target cluster versus off-target clusters (Aevermann et al. 2021, Liu et al. 2024). Binary " +
  "score ranges from 0 to 1. Binary gene set is the top 10 genes with the highest binary scores.";

export const fieldSections = {
  // Section order and slot names follow the node card specification sheet
  // (09/16) attached to nlm-ckn#335. Slots the sheet asks for that no CS
  // document carries are omitted rather than configured, so they cannot render
  // as blank rows: embedding, disease and the F-beta score appear on the
  // related dataset and biomarker combination cards, the binary score on the
  // related binary gene set card; PMID, SKOS mapping and per-gene binary scores
  // are on no node.
  CS: [
    {
      section: "Context",
      fields: [
        { key: "author_cell_term", label: "Author cell set annotation" },
        { key: "publication", label: "Publication" },
      ],
    },
    {
      section: "Provenance",
      fields: [
        { key: "cellxgene_collection", label: "CELLxGENE collection" },
        { key: "dataset_name", label: "Dataset name" },
        // Not a page: every cell set's dataset URL is a raw .h5ad, so the label
        // has to warn before the click does.
        { key: "cellxgene_dataset", label: "CELLxGENE data download (.h5ad)" },
      ],
    },
    {
      section: "Analysis Metadata",
      fields: [{ key: "cluster_annotation", label: "Annotation level" }],
    },
    CKN_FILTERING_CRITERIA,
    {
      section: "Post-filtering Cell Set Metadata",
      fields: [
        { key: "species", label: "Species" },
        { key: "anatomical_structure", label: "Anatomical Structure Collection" },
        // Only ~28% of cell sets carry a Cell Ontology term; the rest omit the row.
        { key: "ontology_purl", label: "Cell Type" },
      ],
    },
    {
      section: "Post-filtering Cell Set Statistics",
      fields: [
        { key: "cell_count", label: "Cell count" },
        { key: "median_silhouette", label: "Median silhouette score" },
      ],
    },
    {
      section: "Markers & Selectively Expressed Genes",
      fields: [
        { key: "biomarker_combination", label: "Biomarker combination" },
        { key: "binary_gene_set", label: "Binary gene set" },
      ],
    },
    {
      // The NS-Forest metrics that score the biomarker combination. Kept
      // adjacent because the scores are meaningless without the combination
      // they grade.
      section: "Biomarker Combination Metrics",
      fields: [
        { key: "precision", label: "Precision: TP/(TP+FP)" },
        { key: "recall", label: "Recall: TP/(TP+FN)" },
        { key: "on_target", label: "On-target fraction" },
        { key: "true_positive", label: "True positives (TP)" },
        { key: "false_positive", label: "False positives (FP)" },
        { key: "false_negative", label: "False negatives (FN)" },
        // No document carries true_negative: NS-Forest does not use it.
        { key: "true_negative", label: "True negatives (TN)", value: "Not used in calculation" },
      ],
    },
  ],
  // Section order and slot names follow the node card specification sheet
  // attached to nlm-ckn#327. Slots the sheet asks for that no CSD document
  // carries — PMID and sex — are omitted rather than configured, so they
  // cannot render as blank rows; they are tracked as data gaps on that issue.
  CSD: [
    {
      section: "Context",
      // Citation reads "Muto (2021) Nat Commun"; the collection map hangs the
      // DOI URL off the separate `publication` key.
      fields: [{ key: "Citation", label: "Publication" }],
    },
    {
      section: "Provenance",
      fields: [
        { key: "cellxgene_collection", label: "CELLxGENE collection" },
        { key: "dataset_name", label: "Dataset name" },
        // Not a page: this URL serves the raw .h5ad, hundreds of MB for a
        // typical dataset, so the label has to warn before the click does.
        { key: "cellxgene_dataset", label: "CELLxGENE data download (.h5ad)" },
      ],
    },
    {
      section: "Analysis Metadata",
      fields: [
        { key: "cluster_annotation", label: "Cluster annotation level" },
        { key: "embedding", label: "Embedding" },
      ],
    },
    CKN_FILTERING_CRITERIA,
    {
      section: "Post-filtering Dataset Metadata",
      fields: [
        { key: "species", label: "Species" },
        {
          key: "anatomical_structure",
          label: "Anatomical Structure Collection",
          transform: humanizeSlug,
        },
        { key: "tissue_annotation", label: "Tissue" },
        { key: "disease_status", label: "Disease" },
        { key: "donor_age", label: "Age" },
        // Still a bare EFO CURIE: no EFO collection exists in either graph, so
        // the UI has nothing to resolve the term name against (nlm-ckn#311).
        { key: "assay_summary", label: "Assay" },
      ],
    },
    {
      section: "Post-filtering Dataset Statistics",
      fields: [
        { key: "donor_id_count", label: "Donor count" },
        { key: "filtered_cell_count", label: "Cell count" },
        { key: "cluster_summary", label: "Cluster count" },
        { key: "median_of_median_silhouette", label: "Median of median silhouette score" },
        { key: "median_of_f_beta_scores", label: "Median F-beta score" },
      ],
    },
    {
      // The sheet's "Additional" is the four secondary quality statistics. Any
      // other populated attribute is merged in here by getSectionedFields, so
      // naming these does not hide the rest.
      section: "Additional",
      fields: [
        { key: "mean_silhouette", label: "Mean of median silhouette score" },
        {
          key: "standard_deviation_of_silhouette",
          label: "Standard deviation of median silhouette score",
        },
        { key: "mean_f_beta_score", label: "Mean F-beta score" },
      ],
    },
  ],
  // A biomarker combination and a binary gene set carry only their markers and
  // one score. The cell set they belong to, with its context and the rest of
  // the NS-Forest metrics, shows as a related card.
  BMC: [
    {
      section: "Markers & Selectively Expressed Genes",
      info: MARKERS_INFO,
      fields: [{ key: "markers", label: "Biomarker combination" }],
    },
    {
      section: "Biomarker Combination Metrics",
      info: BIOMARKER_METRICS_INFO,
      fields: [{ key: "f_beta_score", label: "F-beta score" }],
    },
  ],
  BGS: [
    {
      section: "Markers & Selectively Expressed Genes",
      info: MARKERS_INFO,
      fields: [{ key: "markers", label: "Binary gene set" }],
    },
    {
      section: "Binary Score",
      info: BINARY_SCORE_INFO,
      // The sheet asks for the median and a score per gene; the data carries
      // only the mean, so the row names what it shows.
      fields: [{ key: "mean_binary_score", label: "Mean binary score" }],
    },
  ],
  // Slots follow the Publication sheet attached to nlm-ckn#330. PMID is left
  // out because no PUB document carries it. The citation stays plain text: the
  // card shows only what the node holds, and the collection map puts no URL on
  // Citation.
  PUB: [
    {
      section: "Overview",
      fields: [
        { key: "Citation", label: "Publication" },
        { key: "author_list", label: "Authors" },
        { key: "year", label: "Year" },
        { key: "title", label: "Title" },
        { key: "journal", label: "Journal" },
      ],
    },
  ],
};

/**
 * Collection-map attributes the node card leaves out of the "Additional"
 * catch-all, keyed by collection abbreviation.
 */
export const omittedFields = {
  // The specification drops the pre-filtering total; every other figure on the
  // card is post-filtering, so showing it in the catch-all invites a misread.
  CSD: ["cell_count"],
  // The specification removes the DOI row.
  PUB: ["publication_doi"],
  // expressed_genes repeats binary_gene_set, cluster_cell_count repeats
  // cell_count, and silhouette_score repeats median_silhouette, in every cell
  // set.
  CS: ["expressed_genes", "cluster_cell_count", "silhouette_score"],
};

/**
 * Collections whose documents a node card shows in full under "Related", keyed
 * by the card's collection abbreviation, in display order. A card's own rows
 * show only its own document; a neighbor's values appear only as its own card.
 * Each pair is joined by exactly one edge, so each lists at most one document.
 */
export const relatedCollections = {
  // The dataset leads: it is the best context for a cell set.
  CS: ["CSD", "BMC", "BGS"],
  BMC: ["CS"],
  BGS: ["CS"],
};
